/**
 * ValidationKpiDashboard — top-of-results summary panel.
 *
 * Renders the headline KPIs (compliance %, total checks, failures,
 * warnings, coverage, schema badge) + per-category horizontal stacked
 * bars + a single-line governance strip (rules active / checks applied
 * / skipped / duration). All numbers are pre-aggregated by the audit
 * pipeline; this component is pure presentation + a handful of sums.
 *
 * Created Feb 28, 2026 per EMERGENT_WORK_ORDER_FRONTEND_KPI.md.
 * Reuses ``card-elevated`` + ``hsl(var(--success|warning|destructive))``
 * tokens already established by ``CategoryBreakdown.js`` so the
 * dashboard feels native to the page.
 *
 * Props
 * -----
 *   audit              - full ``audit`` object from /api/audits/{id}/results.
 *                        We read: total_elements, parsed_elements,
 *                        validated_elements, validation_coverage_percent,
 *                        validation_mode, schema_version,
 *                        active_rule_count, applicable_checks_count,
 *                        skipped_rule_count, audit_duration_seconds,
 *                        ifc_file_name, bep_file_name, failure_reason,
 *                        status.
 *   categoryBreakdown  - the ``category_breakdown`` array from the same
 *                        response. Each item: {category, label, total,
 *                        passed, warnings, failures, skipped}.
 */
import { AlertCircle, Activity, Wrench, Cpu } from 'lucide-react';
import {
  resolveApplicableCheckCompliance,
  resolveElementComplianceMetric,
  resolveModelPreflightCompliance,
} from '../lib/auditCompliance';

// ── Helpers ────────────────────────────────────────────────────────────

function _complianceColor(pct) {
  if (!Number.isFinite(pct)) return 'text-foreground';
  if (pct >= 90) return 'text-[hsl(var(--success))]';
  if (pct >= 70) return 'text-[hsl(var(--warning))]';
  return 'text-[hsl(var(--destructive))]';
}

const _isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const _formatRate = (value) => (_isNumber(value) ? `${value.toFixed(2)}%` : 'Unavailable');

function _summaryMetric(metric, passKey = 'pass', failKey = 'fail') {
  if (!metric || !_isNumber(metric.assessed)) return { available: false };
  const pass = metric[passKey];
  const fail = metric[failKey];
  const skipped = metric.skipped;
  if (metric.assessed === 0) {
    return { available: true, assessed: 0, pass, fail, skipped, state: _isNumber(skipped) && skipped > 0 ? 'N/A' : 'Not assessed' };
  }
  const rate = _isNumber(metric.pass_rate)
    ? metric.pass_rate
    : (_isNumber(pass) && _isNumber(fail) ? (pass / (pass + fail)) * 100 : null);
  return { available: _isNumber(rate), assessed: metric.assessed, pass, fail, skipped, rate };
}

// Compact pill for the IFC schema version. Re-used from this file by
// ── Schema badge — exported separately so other surfaces can reuse it
// without having to render the whole KPI dashboard.
//
// ResultsPage.js (named export) so the look is identical wherever the
// schema is shown.
export function SchemaBadge({ schemaVersion, className = '' }) {
  const empty = !schemaVersion;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
        empty
          ? 'border-border text-muted-foreground'
          : 'border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
      } ${className}`}
      title={empty ? 'IFC schema version not detected' : `IFC schema: ${schemaVersion}`}
      data-testid="schema-version-badge"
    >
      {empty ? '—' : schemaVersion}
    </span>
  );
}

// ── Authoring-tool badge — Mar 2, 2026 ──────────────────────────────
// Renders the IFC authoring software (e.g. "ARCHICAD 25.0.0 · GRAPHISOFT")
// next to the schema chip on the AIM Register. ``tool`` is the
// ``summary.authoring_tool`` dict produced by services/composition.py.
// Tooltip = raw STEP-header ``originating_system`` so reviewers can
// see the unprocessed string when the IfcApplication entity was missing.
export function AuthoringToolBadge({ tool, className = '' }) {
  const display = tool?.display || 'Authoring tool: unknown';
  const isUnknown = !tool || tool.source === 'unknown' || display === 'Authoring tool: unknown';
  const tip = tool?.originating_system
    ? `STEP header: ${tool.originating_system}`
    : (isUnknown ? 'No IfcApplication and no STEP header originating_system' : display);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded border ${
        isUnknown
          ? 'border-border text-muted-foreground'
          : 'border-[hsl(var(--accent-foreground))]/40 text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent-foreground))]/5'
      } ${className}`}
      title={tip}
      data-testid="authoring-tool-badge"
    >
      <Wrench className="h-3 w-3" aria-hidden />
      <span className="truncate max-w-[260px]">{display}</span>
    </span>
  );
}

export function ModelBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 ${className}`}
      title="AI-generated content: GPT-5.6"
      data-testid="model-badge"
    >
      <Cpu className="h-3 w-3" aria-hidden />
      GPT-5.6
    </span>
  );
}


// ── Sub-components ─────────────────────────────────────────────────────

function KpiCard({ label, value, accent = '', subline = '', testid, title = '' }) {
  return (
    <div
      className="card-elevated p-4 flex flex-col gap-1"
      data-testid={testid}
      title={title || undefined}
    >
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`text-xl font-bold leading-tight ${accent}`}>{value}</p>
      {subline && (
        <p className="text-[11px] text-muted-foreground">{subline}</p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────

export function ValidationKpiDashboard({ audit }) {
  if (!audit) return null;

  // Failed-audit state: surface the failure_reason in an error card and
  // bail out of the KPI render path.
  if (audit.status === 'failed') {
    return (
      <div
        className="card-elevated p-4 mb-6 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5"
        data-testid="kpi-dashboard-failed"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[hsl(var(--destructive))] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-[hsl(var(--destructive))]">
              Validation did not complete
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {audit.failure_reason || 'No additional details available.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In-progress state: a quiet placeholder. The page's existing live
  // progress widget renders elsewhere — we just keep our slot reserved.
  if (audit.status && audit.status !== 'completed') {
    return (
      <div
        className="card-elevated p-4 mb-6 flex items-center gap-3 text-muted-foreground"
        data-testid="kpi-dashboard-pending"
      >
        <Activity className="h-4 w-4 animate-pulse" />
        <p className="text-sm">
          Validation in progress — KPIs will appear when the audit completes.
        </p>
      </div>
    );
  }

  const totalElements = audit.total_elements;
  const parsedElements = audit.parsed_elements;
  const applicableResolution = resolveApplicableCheckCompliance(audit.summary);
  const elementResolution = resolveElementComplianceMetric(audit.summary, totalElements);
  const modelResolution = resolveModelPreflightCompliance(audit.summary);
  const activeBep = _summaryMetric(applicableResolution.metric);
  const elementCompliance = _summaryMetric(elementResolution.metric);
  const elementRuleCompliance = _summaryMetric(
    audit.summary?.element_rule_compliance || applicableResolution.metric
  );
  const modelCompliance = _summaryMetric(modelResolution.metric, 'passed', 'failed');
  const historicalApplicable = applicableResolution.source === 'category_breakdown.custom_bep_rules';
  const processingCoverage = _isNumber(totalElements) && _isNumber(parsedElements) && totalElements > 0
    ? (parsedElements / totalElements) * 100
    : null;
  const assessmentCoverage = _isNumber(totalElements) && totalElements > 0 && elementCompliance.available
    ? (elementCompliance.assessed / totalElements) * 100
    : null;
  const knownFailureCounts = [elementRuleCompliance, modelCompliance].filter((metric) => metric.available && _isNumber(metric.fail));
  const totalFailures = knownFailureCounts.length > 0
    ? knownFailureCounts.reduce((total, metric) => total + metric.fail, 0)
    : null;

  // Mar 2, 2026 — Dominant-discipline badge. Inferred from the ISO
  // 19650 file-name suffix (block 8 onward after the 7th dash). The
  // role letter — ``M`` / ``A`` / ``S`` / ``E`` — pins the audit to a
  // single discipline so reviewers can scan the KPI strip without
  // reading the full filename. Falls silent (no card rendered) when
  // the file-name doesn't match a known role.
  const disc = (() => {
    const name = audit.ifc_file_name || '';
    const parts = name.replace(/\.ifc$/i, '').split('-');
    const suffix = parts.length >= 8 ? parts.slice(7).join('-') : '';
    if (/^M-/i.test(suffix)) return { label: 'MEP',          color: '#BA7517' };
    if (/^A-/i.test(suffix)) return { label: 'Architecture', color: '#3B6D11' };
    if (/^S-/i.test(suffix)) return { label: 'Structure',    color: '#185FA5' };
    if (/^E-/i.test(suffix)) return { label: 'Electrical',   color: '#7B1FA2' };
    return null;
  })();

  return (
    <div className="space-y-4 mb-6" data-testid="kpi-dashboard">
      {/* ── KPI cards row ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <KpiCard
          label="Applicable-check pass rate"
          value={activeBep.available ? (activeBep.state || _formatRate(activeBep.rate)) : 'Unavailable'}
          accent={_complianceColor(activeBep.rate)}
          subline={activeBep.available && !activeBep.state && _isNumber(activeBep.pass)
            ? `${activeBep.pass.toLocaleString()} of ${activeBep.assessed.toLocaleString()} ${historicalApplicable ? 'historical applicable checks' : 'active snapshot checks'} passed`
            : 'Active confirmed-rule snapshot unavailable'}
          testid="kpi-compliance"
          title="Active confirmed-rule snapshot only. SKIPPED, META, and QG supplementary results are excluded."
        />
        <KpiCard
          label="Applicable checks"
          value={activeBep.available ? activeBep.assessed.toLocaleString() : 'Unavailable'}
          subline={activeBep.state
            ? 'No applicable checks'
            : historicalApplicable ? 'Historical applicable BEP aggregate' : 'Active confirmed-rule snapshot'}
          testid="kpi-total-checks"
        />
        <KpiCard
          label="Failed checks"
          value={totalFailures != null ? totalFailures.toLocaleString() : 'Unavailable'}
          accent={totalFailures > 0 ? 'text-[hsl(var(--destructive))]' : 'text-foreground'}
          subline={[
            elementRuleCompliance.available && _isNumber(elementRuleCompliance.fail) ? `Element-rule: ${elementRuleCompliance.fail.toLocaleString()}` : null,
            modelCompliance.available && _isNumber(modelCompliance.fail) ? `Model/file: ${modelCompliance.fail.toLocaleString()}` : null,
          ].filter(Boolean).join(' · ') || 'Breakdown unavailable'}
          testid="kpi-failures"
          title="Element-rule and model/file failures are shown only when their stored aggregates are available."
        />
        <KpiCard
          label="Assessment coverage"
          value={assessmentCoverage == null ? 'Unavailable' : elementCompliance.assessed === 0 ? 'Not assessed' : _formatRate(assessmentCoverage)}
          subline={assessmentCoverage == null
            ? 'Assessed-elements aggregate unavailable'
            : `${elementCompliance.assessed.toLocaleString()} of ${totalElements.toLocaleString()} IFC elements assessed`}
          testid="kpi-assessment-coverage"
          title="Assessed elements ÷ total IFC elements. This is separate from IFC processing coverage."
        />
        <KpiCard
          label="IFC processing coverage"
          value={processingCoverage == null ? 'Unavailable' : _formatRate(processingCoverage)}
          subline={processingCoverage == null
            ? 'Parsed/total element counts unavailable'
            : `${parsedElements.toLocaleString()} of ${totalElements.toLocaleString()} IFC elements parsed`}
          testid="kpi-processing-coverage"
        />
        <KpiCard
          label="Model & File Checks"
          value={modelCompliance.available ? (modelCompliance.state || _formatRate(modelCompliance.rate)) : 'Unavailable'}
          accent={_complianceColor(modelCompliance.rate)}
          subline={modelCompliance.available && !modelCompliance.state && _isNumber(modelCompliance.pass)
            ? `${modelCompliance.pass.toLocaleString()} of ${modelCompliance.assessed.toLocaleString()} assessed · ${_isNumber(modelCompliance.skipped) ? `${modelCompliance.skipped} skipped` : 'skip count unavailable'}`
            : modelCompliance.state === 'N/A' ? 'Skipped model/file outcomes only' : 'Model/file aggregate unavailable'}
          testid="kpi-model-file-checks"
        />
        <KpiCard
          label="Schema"
          value={<SchemaBadge schemaVersion={audit.schema_version} />}
          subline={
            audit.summary?.authoring_tool?.display ? (
              // Mar 2, 2026 — Show the authoring tool in the subline;
              // the filename moves to the ``title`` tooltip so it's
              // still one keyboard-hover away.
              <span
                title={audit.ifc_file_name || ''}
                className="cursor-help"
                data-testid="kpi-schema-authoring-tool"
              >
                {audit.summary.authoring_tool.display}
              </span>
            ) : (audit.ifc_file_name || '')
          }
          testid="kpi-schema"
        />
        {/* Mar 2, 2026 — 7th KPI card. Discipline badge driven by
            ``disc`` (above). Only renders when the file-name suffix
            resolved to a known role so audits without an ISO 19650
            file-name still show a clean 6-card row. */}
        {disc && (
          <div
            className="card-elevated p-4 flex flex-col gap-1"
            data-testid="kpi-discipline"
          >
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Discipline
            </p>
            <p
              className="text-2xl font-bold leading-tight"
              style={{ color: disc.color }}
            >
              {disc.label}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Inferred from file name
            </p>
          </div>
        )}
      </div>

      {(elementCompliance.available || modelCompliance.available) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground" data-testid="kpi-secondary-compliance">
          {elementCompliance.available && (
            <span data-testid="kpi-element-compliance">
              Assessed-element compliance: {elementCompliance.state || (_isNumber(elementCompliance.pass)
                ? `${elementCompliance.pass}/${elementCompliance.assessed} (${_formatRate(elementCompliance.rate)})`
                : 'Unavailable')}
            </span>
          )}
          {modelCompliance.available && (
            <span data-testid="kpi-model-preflight-compliance">
              Model & File Checks: {modelCompliance.state || (_isNumber(modelCompliance.pass)
                ? `${modelCompliance.pass}/${modelCompliance.assessed} (${_formatRate(modelCompliance.rate)})`
                : 'Unavailable')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ValidationKpiDashboard;
